import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { EmployeeService } from '../../services/employee.service';
import { LogService } from '../../services/log.service';
import { RoleService } from '../../services/role.service';

// Importaciones de interfaces.
import { IEmployee } from '../../interfaces/employee.interface';
import { IRole } from '../../interfaces/role.interface';
import { IRoleResponse } from '../../interfaces/role-response.interface';

// Importaciones de modelos.
import { Employee } from '../../models/employee.model';
import { Log } from '../../models/log.model';
import { Role } from '../../models/role.model';

/**
 * Componente de diálogo para la actualización del empleado.
 *
 * Este componente proporciona una interfaz para actualizar la información de un empleado.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos del empleado seleccionado.
 */
@Component({
  selector: 'app-employee-update-dialog',
  templateUrl: './employee-update-dialog.component.html',
  standalone: false,
})
export class EmployeeUpdateDialogComponent implements OnInit {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de actualización de empleados.
   */
  private readonly dialogRef: MatDialogRef<EmployeeUpdateDialogComponent> =
    inject(MatDialogRef<EmployeeUpdateDialogComponent>);

  /**
   * Datos del empleado.
   *
   * Contiene la información del empleado que se está actualizando, obtenida a través de la inyección de dependencias.
   */
  private readonly employee: IEmployee = inject<IEmployee>(MAT_DIALOG_DATA);

  /**
   * Indica si la carga de roles está en curso.
   *
   * Se utiliza para mostrar un indicador de carga mientras se obtienen los roles disponibles.
   */
  loadingRoles: boolean = false;

  /**
   * Lista de roles disponibles.
   *
   * Contiene los roles que pueden asignarse al empleado en el proceso de actualización.
   */
  roles: IRole[] = [];

  /**
   * Indica si la operación de actualizar está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se actualiza la información del empleado.
   */
  loadingUpdate: boolean = false;

  /**
   * Lista de errores al actualizar.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización de empleado.
   *
   * Gestiona los campos y validaciones necesarias para actualizar la información del empleado.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    employeeId: new FormControl('', Validators.required),
    roleId: new FormControl('', Validators.required),
    displayName: new FormControl(),
    username: new FormControl('', Validators.required),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
    ]),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'employeeId'.
   *
   * Retorna el control asociado a la propiedad 'employeeId' del formulario,
   * que gestiona la identificación del empleado.
   *
   * @returns El control de formulario para 'employeeId'.
   */
  get formEmployeeId(): AbstractControl | null {
    return this.form.get('employeeId');
  }

  /**
   * Obtiene el control del formulario para el campo 'roleId'.
   *
   * Retorna el control asociado a la propiedad 'roleId' del formulario,
   * que gestiona el rol del empleado.
   *
   * @returns El control de formulario para 'roleId'.
   */
  get formRoleId(): AbstractControl | null {
    return this.form.get('roleId');
  }

  /**
   * Obtiene el control del formulario para el campo 'username'.
   *
   * Retorna el control asociado a la propiedad 'username' del formulario,
   * que gestiona el nombre de usuario del empleado.
   *
   * @returns El control de formulario para 'username'.
   */
  get formUsername(): AbstractControl | null {
    return this.form.get('username');
  }

  /**
   * Obtiene el control del formulario para el campo 'email'.
   *
   * Retorna el control asociado a la propiedad 'email' del formulario,
   * que gestiona la dirección de correo electrónico del empleado.
   *
   * @returns El control de formulario para 'email'.
   */
  get formEmail(): AbstractControl | null {
    return this.form.get('email');
  }

  /**
   * Obtiene el control del formulario para el campo 'displayName'.
   *
   * Retorna el control asociado a la propiedad 'displayName' del formulario,
   * que gestiona el nombre visible o de pantalla del empleado.
   *
   * @returns El control de formulario para 'displayName'.
   */
  get formDisplayName(): AbstractControl | null {
    return this.form.get('displayName');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param employeeService - Servicio para gestionar la información de los empleados.
   * @param logService - Servicio para gestionar los registros.
   * @param roleService - Servicio para gestionar los roles de los empleados.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private employeeService: EmployeeService,
    private logService: LogService,
    private roleService: RoleService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta cuando el componente es inicializado. Es utilizado para realizar tareas
   * de configuración o cargar datos necesarios para la operación del componente.
   */
  async ngOnInit(): Promise<void> {
    this.loadingRoles = true;

    try {
      const roleResponse: IRoleResponse = await this.roleService.getAll();
      this.roles = roleResponse.roles.map((role: IRole) => {
        return new Role(role);
      });
    } finally {
      this.loadingRoles = false;
    }

    this.formEmployeeId?.setValue(this.employee.id);
    this.formRoleId?.setValue(this.employee.role.id);
    this.formStatus?.setValue(!this.employee.disabled);
    this.formDisplayName?.setValue(this.employee.displayName);
    this.formUsername?.setValue(this.employee.username);
    this.formEmail?.setValue(this.employee.email);
  }

  /**
   * Actualiza los datos del empleado.
   *
   * Este método se encarga de realizar el proceso de actualizar la información del empleado.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async update(): Promise<void> {
    this.loadingUpdate = true;
    this.errors = [];

    const employee: IEmployee = new Employee({
      id: Number(this.formEmployeeId?.value),
      role: new Role({ id: Number(this.formRoleId?.value) }),
      username: this.formUsername?.value,
      email: this.formEmail?.value,
      displayName: this.formDisplayName?.value,
      disabled: Number(!this.formStatus?.value),
    });

    try {
      const employeeResponse: string = await this.employeeService.update(
        employee
      );

      // Añadir un registro de actualización del empleado.
      try {
        await this.logService.create(
          new Log({
            description: `Ha actualizado el empleado '${this.employee.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de actualización del empleado.'
          );
        }
      }

      this.snackBar.open(employeeResponse, '', {
        duration: 5000,
      });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loadingUpdate = false;
  }
}
